/**
 * Global refinement bar — transaction-level context questions.
 *
 * Renders ONE chip-row per context_type (portal_channel, subcategory,
 * transaction_type, ecosystem). Options are the UNION of every chip value
 * referenced by any relevant rule on any card in the wallet. Friendly labels
 * come from the context_definitions registry.
 *
 * Strict subcategory rule:
 *   • Subcategory chips are DISABLED until a portal_channel is selected.
 *   • Once a portal is selected, only subcategory values that CO-OCCUR with
 *     that portal in some rule are enabled. Irrelevant subcategories are
 *     visually greyed-out so the user can see them but can't accidentally
 *     pick an invalid combination (e.g. HSBC TravelPortal + IHCL hotels).
 *
 * Selecting a chip pins that context for the WHOLE transaction. The engine
 * then applies it consistently to every card's rule evaluation.
 */
import { Label } from '@/components/ui/label';
import { Lock } from 'lucide-react';
import { cn } from '@/lib/utils';

const ChipRow = ({ options, value, onChange, testIdPrefix, disabledChipIds }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}-chips`}>
    <button
      type="button"
      onClick={() => onChange(null)}
      className={cn(
        'inline-flex items-center px-3 py-1.5 rounded-full text-xs border transition-colors',
        !value
          ? 'bg-accent text-accent-foreground border-accent'
          : 'bg-transparent text-foreground/80 border-border hover:bg-muted'
      )}
      data-testid={`${testIdPrefix}-chip-none`}
    >
      Not applicable
    </button>
    {options.map((opt) => {
      const selected = value === opt.id;
      const isDisabled = (disabledChipIds || new Set()).has(opt.id);
      return (
        <button
          key={opt.id}
          type="button"
          onClick={() => {
            if (isDisabled) return;
            onChange(selected ? null : opt.id);
          }}
          disabled={isDisabled}
          aria-disabled={isDisabled}
          className={cn(
            'inline-flex items-center px-3 py-1.5 rounded-full text-xs border transition-colors',
            isDisabled
              ? 'opacity-40 cursor-not-allowed border-border text-muted-foreground'
              : selected
              ? 'bg-accent text-accent-foreground border-accent'
              : 'bg-transparent text-foreground/80 border-border hover:bg-muted hover:text-foreground'
          )}
          data-testid={`${testIdPrefix}-chip-${opt.id}`}
          title={
            isDisabled
              ? 'Not relevant to the selected portal'
              : `Used by ${(opt.source_card_ids || []).length} card${(opt.source_card_ids || []).length === 1 ? '' : 's'}`
          }
        >
          {opt.label}
        </button>
      );
    })}
  </div>
);

const _humanizeContextType = (ct) => {
  if (ct === 'portal_channel') return 'Which portal / channel?';
  if (ct === 'subcategory') return 'Which subcategory?';
  if (ct === 'transaction_type') return 'Transaction type';
  if (ct === 'ecosystem') return 'Ecosystem';
  return (ct || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

export const GlobalRefinement = ({ globalQuestions, answers, onChange, portalSubcategoryMap }) => {
  const questions = globalQuestions || [];
  if (!questions.length) return null;
  const contexts = answers?.contexts || {};
  const selectedPortal = contexts.portal_channel || null;

  // Compute the set of subcategory values RELEVANT to the selected portal.
  // - No portal selected → entire subcategory row is locked (visually).
  // - Portal selected → values not in portalSubcategoryMap[portal] are greyed out.
  const relevantSubcats = selectedPortal && portalSubcategoryMap
    ? new Set(Array.from(portalSubcategoryMap[selectedPortal] || []))
    : null;

  return (
    <div
      className="border border-border/70 rounded-xl bg-muted/30 p-4 sm:p-5 space-y-4"
      data-testid="global-refinement"
    >
      <div className="flex items-baseline justify-between">
        <h3 className="text-sm font-semibold text-foreground">Transaction details</h3>
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
          shared across all cards
        </span>
      </div>
      {questions.map((q) => {
        const isSubcategory = q.context_type === 'subcategory';
        const lockedNoPortal = isSubcategory && !selectedPortal;
        // For subcategory, compute the disabled set:
        // - If no portal selected → ALL chips disabled.
        // - If portal selected → chips NOT in relevantSubcats are disabled.
        const disabledChipIds = !isSubcategory
          ? null
          : lockedNoPortal
          ? new Set(q.options.map((o) => o.id))
          : new Set(q.options.filter((o) => !relevantSubcats.has(o.id)).map((o) => o.id));

        return (
          <div key={q.context_type} data-testid={`global-q-${q.context_type}`}>
            <Label className="text-xs font-medium text-foreground mb-2 flex items-center gap-2">
              {_humanizeContextType(q.context_type)}
              <span className="text-[10px] uppercase text-muted-foreground tracking-wider">
                {q.options.length} option{q.options.length === 1 ? '' : 's'}
              </span>
              {lockedNoPortal && (
                <span
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-600 dark:text-amber-400"
                  data-testid="subcategory-locked-hint"
                >
                  <Lock className="h-3 w-3" /> Pick a portal first
                </span>
              )}
            </Label>
            <ChipRow
              options={q.options}
              value={contexts[q.context_type] || null}
              onChange={(val) => {
                const nextContexts = { ...contexts };
                if (val) nextContexts[q.context_type] = val;
                else delete nextContexts[q.context_type];
                // Side-effect: when portal changes, clear subcategory if it's
                // no longer compatible with the new portal.
                if (q.context_type === 'portal_channel' && nextContexts.subcategory && portalSubcategoryMap) {
                  const newRelevant = new Set(Array.from(portalSubcategoryMap[val] || []));
                  if (!newRelevant.has(nextContexts.subcategory)) {
                    delete nextContexts.subcategory;
                  }
                }
                onChange({ ...answers, contexts: nextContexts });
              }}
              testIdPrefix={`global-q-${q.context_type}`}
              disabledChipIds={disabledChipIds}
            />
          </div>
        );
      })}
    </div>
  );
};
